import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { JQRItem, Progress, RubricGrades, LibraryItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@flowcore-water/ui';
import { libraryService } from '../services/libraryService';
interface JQRItemCardProps {
  item: JQRItem;
  qualificationId: string;
  progress: Progress | undefined;
  onUpdate: (progress: Progress) => Promise<void>;
  isInstructor: boolean;
  retryNumber?: number;
}

const RUBRIC_LABELS: Record<keyof RubricGrades, string> = {
  prepare: 'Prepare',
  present: 'Present',
  greet: 'Greet',
  execute: 'Execute',
  explore: 'Explore',
  wrapup: 'Wrapup',
};

const EMPTY_RUBRIC: RubricGrades = {
  prepare: null,
  greet: null,
  explore: null,
  present: null,
  execute: null,
  wrapup: null,
};

// Canonical implementation lives in skillProgress; re-export for backwards compat
import { isRubricFail } from '../utils/skillProgress';
export { isRubricFail };

/** Markdown text field that shows rendered markdown when not focused */
const MarkdownField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  className?: string;
  style?: React.CSSProperties;
  previewClassName?: string;
}> = ({ value, onChange, disabled, placeholder, rows = 3, className, style, previewClassName }) => {
  const { t } = useTheme();
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  if (!editing && value) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={`cursor-text ${previewClassName || 'prose prose-invert prose-sm max-w-none rounded-lg border /40 px-3 py-2'}`}
        style={style}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
      </div>
    );
  }

  if (!editing && !value) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={className}
        style={style}
      >
        <span className=" italic text-sm" style={{ color: t.textMuted }}>{placeholder || 'Click to add notes...'}</span>
      </div>
    );
  }

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setEditing(false)}
      disabled={disabled}
      className={className}
      style={style}
      rows={rows}
      placeholder={placeholder}
    />
  );
};

export const JQRItemCard: React.FC<JQRItemCardProps> = ({
  item,
  qualificationId,
  progress,
  onUpdate,
  isInstructor,
  retryNumber,
}) => {
  const { user } = useAuth();
  const { t } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [notes, setNotes] = useState(progress?.notes || '');
  const [relatedArticles, setRelatedArticles] = useState<LibraryItem[]>([]);
  const [rubric, setRubric] = useState<RubricGrades>(progress?.rubric || { ...EMPTY_RUBRIC });
  const [rubricNotes, setRubricNotes] = useState<Record<string, string>>(progress?.rubricNotes || {});
  const [saved, setSaved] = useState(false);
  const [signoffError, setSignoffError] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState<string>(
    progress?.completedAt ? new Date(progress.completedAt).toISOString().split('T')[0] : ''
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use refs so auto-save always reads latest values (avoids stale closures)
  const notesRef = useRef(notes);
  const rubricRef = useRef(rubric);
  const rubricNotesRef = useRef(rubricNotes);
  const completionDateRef = useRef(completionDate);

  // Ref for the latest progress prop — auto-save reads completed state from here
  const progressRef = useRef(progress);

  const hasRubric = item.hasRubric && item.category === 'ridealong';
  const isCompleted = progress?.completed || false;
  const isFailed = hasRubric && isRubricFail(progress?.rubric || null);

  // Keep refs in sync with state
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { rubricRef.current = rubric; }, [rubric]);
  useEffect(() => { rubricNotesRef.current = rubricNotes; }, [rubricNotes]);
  useEffect(() => { completionDateRef.current = completionDate; }, [completionDate]);
  useEffect(() => { progressRef.current = progress; }, [progress]);

  // Load related library items (linked at qualification level)
  useEffect(() => {
    libraryService.getAll().then((items) => {
      setRelatedArticles(items.filter((a) => a.linkedQualIds.includes(qualificationId)));
    });
  }, [qualificationId]);

  // Sync state when progress prop changes (only when not mid-save)
  const syncingRef = useRef(false);
  useEffect(() => {
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    setNotes(progress?.notes || '');
    setRubric(progress?.rubric || { ...EMPTY_RUBRIC });
    setRubricNotes(progress?.rubricNotes || {});
    setCompletionDate(progress?.completedAt ? new Date(progress.completedAt).toISOString().split('T')[0] : '');
  }, [progress]);

  // Build progress snapshot from refs — always reads latest values
  const buildProgressSnapshot = useCallback(
    (overrides?: Partial<Progress>): Progress => {
      const p = progressRef.current;
      return {
        qualificationId,
        itemId: retryNumber ? `${item.id}_retry_${retryNumber}` : item.id,
        category: item.category,
        completed: p?.completed || false,
        completedAt: p?.completedAt || null,
        completedBy: p?.completedBy || null,
        completedByName: p?.completedByName || null,
        notes: notesRef.current,
        grade: p?.grade || null,
        rubric: hasRubric ? rubricRef.current : null,
        rubricNotes: hasRubric ? rubricNotesRef.current : null,
        ...overrides,
      };
    },
    [qualificationId, item, retryNumber, hasRubric]
  );

  // Fire-and-forget auto-save — reads from refs, no focus disruption
  const scheduleAutoSave = useCallback(() => {
    if (!isInstructor) return;
    setSaved(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      syncingRef.current = true;
      onUpdate(buildProgressSnapshot()).then(() => {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      });
    }, 800);
  }, [isInstructor, onUpdate, buildProgressSnapshot]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    scheduleAutoSave();
  };

  const handleRubricChange = (key: keyof RubricGrades, value: number | null) => {
    setRubric((prev) => ({ ...prev, [key]: value }));
    scheduleAutoSave();
  };

  const handleRubricNoteChange = (key: string, value: string) => {
    setRubricNotes((prev) => ({ ...prev, [key]: value }));
    scheduleAutoSave();
  };

  const handleDateChange = (value: string) => {
    setCompletionDate(value);
    // If already completed, auto-save with updated date
    if (progressRef.current?.completed && value) {
      setSaved(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const dateToUse = new Date(value + 'T12:00:00');
        syncingRef.current = true;
        onUpdate(buildProgressSnapshot({ completedAt: dateToUse })).then(() => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        });
      }, 800);
    }
  };

  const handleToggleComplete = async () => {
    if (!isInstructor || !user) return;

    // Block sign-off if rubric indicates failure
    if (hasRubric && !isCompleted && isRubricFail(rubricRef.current)) {
      return; // Can't mark complete when rubric is a fail
    }

    // Cancel any pending auto-save to prevent race condition
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    setSignoffError(null);

    const newCompleted = !isCompleted;
    const dateVal = completionDateRef.current;
    const dateToUse = dateVal ? new Date(dateVal + 'T12:00:00') : new Date();

    const updated = buildProgressSnapshot({
      completed: newCompleted,
      completedAt: newCompleted ? dateToUse : null,
      completedBy: newCompleted ? user.uid : null,
      completedByName: newCompleted ? user.displayName : null,
    });

    // Update the progressRef immediately so any future auto-save reads correct state
    progressRef.current = updated;

    try {
      syncingRef.current = true;
      await onUpdate(updated);
    } catch (error) {
      // Revert optimistic update
      progressRef.current = progress;
      const message = error instanceof Error ? error.message : 'Sign-off failed';
      console.error('[JQRItemCard] Sign-off failed:', error);
      setSignoffError(message);
    }
  };

  // Border & background using theme
  const getBorderColor = () => {
    if (isFailed) return `${t.fail}99`;
    if (isCompleted) return `${t.success}99`;
    return t.border;
  };

  const getBgColor = () => {
    if (isFailed) return t.failBg;
    if (isCompleted) return t.successBg;
    return t.cardBg;
  };

  const retryLabel = retryNumber ? ` (Retry #${retryNumber})` : '';

  return (
    <div
      className="rounded-lg border-2 p-4 transition-all"
      style={{ borderColor: getBorderColor(), background: getBgColor() }}
    >
      <div className="flex items-start gap-4">
        {/* Completion Toggle (instructor only) */}
        {isInstructor && (
          <button
            onClick={handleToggleComplete}
            disabled={hasRubric && !isCompleted && isFailed}
            title={hasRubric && !isCompleted && isFailed ? 'Cannot sign off — rubric indicates failure' : undefined}
            className="mt-1 h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
            style={{
              background: isCompleted ? t.success : 'transparent',
              borderColor: isCompleted ? t.success : isFailed ? t.fail : t.textMuted,
              color: isCompleted ? 'white' : isFailed ? t.fail : t.textMuted,
              opacity: (hasRubric && !isCompleted && isFailed) ? 0.6 : 1,
              cursor: (hasRubric && !isCompleted && isFailed) ? 'not-allowed' : 'pointer',
            }}
          >
            <span className="text-sm font-bold">{isCompleted ? '✓' : isFailed ? '✗' : '○'}</span>
          </button>
        )}

        {/* Non-instructor completed indicator */}
        {!isInstructor && isCompleted && (
          <div
            className="mt-1 h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: t.success }}
          >
            <span className="text-sm font-bold " style={{ color: t.textPrimary }}>✓</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full text-left hover:opacity-75 transition-opacity"
          >
            <div>
              <h4 className="font-semibold" style={{ color: t.textPrimary }}>
                {item.title}{retryLabel}
              </h4>
              {item.description && !retryNumber && (
                <p className="text-sm mt-1" style={{ color: t.textSecondary }}>{item.description}</p>
              )}
            </div>
            <span className="ml-2" style={{ color: t.textMuted }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          </button>

          {/* Related Wiki Articles */}
          {relatedArticles.length > 0 && !retryNumber && (
            <div className="mt-2 flex flex-wrap gap-2">
              {relatedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/library/${article.slug}`}
                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors"
                  style={{
                    color: t.accent,
                    border: `1px solid ${t.accent}50`,
                    background: t.accentBg,
                  }}
                >
                  {article.type === 'pdf' ? '📄' : article.type === 'weblink' ? '🔗' : '📖'} {article.title}
                </Link>
              ))}
            </div>
          )}

          {/* Completion Info (visible to ALL users) */}
          {isCompleted && progress?.completedBy && (
            <div
              className="mt-3 rounded px-3 py-2 text-xs border"
              style={{
                color: t.success,
                borderColor: t.successBorder,
                background: t.successBg,
              }}
            >
              ✓ Signed off by {progress.completedByName} on{' '}
              {new Date(progress.completedAt || 0).toLocaleDateString()}
            </div>
          )}

          {/* Failed indicator */}
          {isFailed && !isCompleted && (
            <div
              className="mt-3 rounded px-3 py-2 text-xs border"
              style={{
                color: t.fail,
                borderColor: t.failBorder,
                background: t.failBg,
              }}
            >
              ✗ Did not pass — {retryNumber ? 'see rubric scores below' : 'retry attempt required'}
            </div>
          )}

          {/* Sign-off error */}
          {signoffError && (
            <div
              className="mt-3 rounded px-3 py-2 text-xs border cursor-pointer"
              style={{
                color: t.fail,
                borderColor: t.failBorder,
                background: t.failBg,
              }}
              onClick={() => setSignoffError(null)}
            >
              Sign-off failed: {signoffError}
            </div>
          )}

          {/* Expanded Content — visible to ALL users */}
          {isExpanded && (
            <div className="mt-4 border-t pt-4 space-y-4" style={{ borderColor: t.borderSubtle }}>

              {/* Date picker for backdating (instructor only) */}
              {isInstructor && (
                <div>
                  <label className="block text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                    Completion Date
                  </label>
                  <input
                    type="date"
                    value={completionDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="rounded-lg border px-3 py-1.5 text-sm focus:outline-none"
                    style={{
                      background: t.inputBg,
                      borderColor: t.inputBorder,
                      color: t.textPrimary,
                      colorScheme: 'dark',
                    }}
                  />
                </div>
              )}

              {/* Notes Section */}
              <div>
                <label className="block text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                  Notes
                </label>
                {isInstructor ? (
                  <MarkdownField
                    value={notes}
                    onChange={handleNotesChange}
                    placeholder="Add notes (markdown supported)..."
                    rows={3}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1"
                    style={{
                      background: t.inputBg,
                      borderColor: t.inputBorder,
                      color: t.textPrimary,
                    }}
                    previewClassName="prose prose-invert prose-sm max-w-none rounded-lg border  px-3 py-2 cursor-text hover:border-cyan-500/50 transition-colors"
                  />
                ) : notes ? (
                  <div
                    className="prose prose-invert prose-sm max-w-none rounded-lg border px-3 py-2"
                    style={{ background: t.inputBg, borderColor: t.borderSubtle }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {notes}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm italic" style={{ color: t.textMuted }}>No notes yet</p>
                )}
              </div>

              {/* Rubric for Ridealongs — 2x3 grid */}
              {hasRubric && (
                <div>
                  <label className="block text-xs font-bold mb-3 uppercase tracking-wide" style={{ color: t.textSecondary }}>
                    Rubric Grades (1-5 scale)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(Object.entries(RUBRIC_LABELS) as [keyof RubricGrades, string][]).map(
                      ([key, label]) => {
                        const score = rubric[key];
                        const stepNote = rubricNotes[key] || '';
                        const isBadScore = score === 1 || score === 2;

                        return (
                          <div
                            key={key}
                            className="rounded-lg border p-3"
                            style={{
                              borderColor: score === 1 ? `${t.fail}66` : score === 2 ? '#f59e0b50' : score === 5 ? `${t.success}80` : t.borderSubtle,
                              background: score === 1 ? t.failBg : score === 5 ? t.successBg : t.cardBg,
                              boxShadow: score === 5 ? `0 0 12px 2px ${t.success}25` : undefined,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <label className="text-sm font-medium w-16 flex-shrink-0" style={{ color: t.textSecondary }}>
                                {label}
                              </label>
                              {isInstructor ? (
                                <select
                                  value={score ?? ''}
                                  onChange={(e) =>
                                    handleRubricChange(
                                      key,
                                      e.target.value ? parseInt(e.target.value) : null
                                    )
                                  }
                                  className="flex-1 rounded border px-1.5 py-1 text-sm focus:outline-none"
                                  style={{
                                    background: t.inputBg,
                                    borderColor: isBadScore ? t.fail : t.inputBorder,
                                    color: isBadScore ? t.fail : t.textPrimary,
                                  }}
                                >
                                  <option value="">—</option>
                                  <option value="1">1</option>
                                  <option value="2">2</option>
                                  <option value="3">3</option>
                                  <option value="4">4</option>
                                  <option value="5">5</option>
                                </select>
                              ) : (
                                <span
                                  className="text-sm font-mono"
                                  style={{
                                    color: score === null
                                      ? t.textMuted
                                      : isBadScore
                                      ? t.fail
                                      : score && score >= 4
                                      ? t.success
                                      : t.textSecondary,
                                  }}
                                >
                                  {score !== null ? `${score}/5` : '—'}
                                </span>
                              )}
                            </div>

                            {/* Per-step notes */}
                            {isInstructor ? (
                              <MarkdownField
                                value={stepNote}
                                onChange={(v) => handleRubricNoteChange(key, v)}
                                placeholder={`Notes...`}
                                rows={2}
                                className="w-full rounded border px-2 py-1 text-xs focus:outline-none resize-none"
                                style={{
                                  background: t.pageBg,
                                  borderColor: t.borderSubtle,
                                  color: t.textSecondary,
                                }}
                                previewClassName="text-xs text-slate-400 rounded border /40 px-2 py-1 cursor-text hover:border-cyan-500/30 transition-colors"
                              />
                            ) : stepNote ? (
                              <div className="text-xs mt-1" style={{ color: t.textSecondary }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {stepNote}
                                </ReactMarkdown>
                              </div>
                            ) : null}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

              {/* Auto-save indicator — fixed height to prevent layout shift */}
              {isInstructor && (
                <div className="pt-1 h-5">
                  <span
                    className={`text-xs transition-opacity duration-300 ${saved ? 'opacity-100' : 'opacity-0'}`}
                    style={{ color: t.success }}
                  >
                    ✓ Saved
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
